import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building2, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [companyName, setCompanyName] = useState('');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await api.checkPaymentStatus(sessionId);
        const data = response.data;
        
        setCompanyName(data.company_name);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
        } else if (data.status === 'expired' || attempts >= maxAttempts) {
          setStatus('failed');
        } else {
          // Continue polling
          setAttempts(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts >= maxAttempts) {
          setStatus('failed');
        } else {
          setAttempts(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        }
      }
    };

    checkStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-[#1E3A8A] rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">CoproEnergy</h1>
          </div>
        </div>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8 text-center">
            {status === 'checking' && (
              <>
                <Loader2 className="w-16 h-16 text-[#1E3A8A] mx-auto mb-4 animate-spin" />
                <h2 className="text-xl font-semibold text-white mb-2">
                  Vérification du paiement...
                </h2>
                <p className="text-slate-400">
                  Veuillez patienter pendant que nous confirmons votre paiement.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-emerald-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">
                  Paiement réussi !
                </h2>
                <p className="text-slate-400 mb-6">
                  Bienvenue {companyName} ! Votre compte est maintenant actif.
                </p>
                <Button 
                  onClick={() => navigate('/login')}
                  className="bg-[#1E3A8A] hover:bg-[#1E40AF]"
                >
                  Se connecter
                </Button>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="w-16 h-16 bg-red-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">
                  Paiement non confirmé
                </h2>
                <p className="text-slate-400 mb-6">
                  Nous n'avons pas pu confirmer votre paiement. 
                  Si vous pensez qu'il s'agit d'une erreur, contactez-nous.
                </p>
                <div className="space-y-2">
                  <Button 
                    onClick={() => navigate('/register')}
                    className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF]"
                  >
                    Réessayer
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/login')}
                    className="w-full border-slate-600 text-white hover:bg-slate-700"
                  >
                    Retour à la connexion
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
